import { SxProps, Theme } from "@mui/material";
import { Box, Skeleton } from "@mui/material";
import signRequestsStyles from "../SignRequests.styles";
import signRequestsTokens, {
  signRequestsResponsiveTokens,
} from "../SignRequests.tokens";
import filtersStyles from "../SignRequestsFilters.styles";
import signRequestsFiltersTokens from "../SignRequestsFilters.tokens";

const styles = signRequestsStyles;
const cells = filtersStyles;
const { avatars, kebab, status } = signRequestsTokens;
const { phone } = signRequestsResponsiveTokens;
const { table, tags } = signRequestsFiltersTokens;

/**
 * The real status chip has no height of its own: it is a `normal`-leading 12px
 * label inside 5px of vertical padding. Derived from the same tokens so a type
 * change carries here too.
 */
const CHIP_HEIGHT = `calc(${status.labelFontSize} * 1.25 + 10px)`;

/**
 * Loading placeholder for `SentRequestRow`.
 *
 * It reuses that row's own style objects — the grid, the lead block, every
 * cell — rather than approximating them, because a skeleton built by hand
 * drifts and the list then jumps each time a page of requests arrives. Since
 * the cells are the real ones, each placeholder also appears and disappears
 * with its column. Anything that paints a background (the icon tile, the
 * status chip, the progress track) is replaced by a Skeleton of the same box
 * instead of the styled element.
 */
const SentRequestRowSkeleton = ({ withTags }: { withTags: boolean }) => (
  <Box aria-hidden="true" sx={styles.requestRow(withTags)}>
    <Box sx={styles.rowLead}>
      {/*
        Sized from the tile's own tokens rather than its style object: that
        object carries a background, which would paint over the Skeleton's.
      */}
      <Skeleton
        variant="rounded"
        sx={{
          width: {
            xs: phone.requestRow.iconTileSize.std,
            sm: table.iconTileSize,
          },
          height: {
            xs: phone.requestRow.iconTileSize.std,
            sm: table.iconTileSize,
          },
          borderRadius: table.iconTileRadius,
          flexShrink: 0,
        }}
      />
      <Box sx={styles.rowText}>
        {/*
          The row's own text styles, not a restatement of them: a text Skeleton
          sizes its box from `fontSize` x line-height, so borrowing the real
          rules is what keeps each placeholder in the same line box as the text
          it stands in for.
        */}
        <Skeleton
          variant="text"
          sx={[styles.rowTitle(false), { width: "62%" }] as SxProps<Theme>}
        />
        <Skeleton
          variant="text"
          sx={[styles.requestSub, { width: "44%" }] as SxProps<Theme>}
        />
      </Box>
    </Box>

    <Box sx={styles.signersColumn}>
      <Box sx={styles.avatarStack}>
        {Array.from({ length: 2 }, (_, index) => (
          <Skeleton
            key={`signer-${index}`}
            variant="circular"
            sx={{
              width: { xs: phone.avatars.size.std, sm: table.avatarSize },
              height: { xs: phone.avatars.size.std, sm: table.avatarSize },
              ml: index === 0 ? 0 : avatars.overlap,
              flexShrink: 0,
            }}
          />
        ))}
      </Box>
    </Box>

    <Box sx={cells.senderCell}>
      <Skeleton variant="text" sx={{ width: "72%" }} />
    </Box>

    <Box sx={styles.statusColumn}>
      <Skeleton
        variant="rounded"
        sx={{
          // The real chip's own placement on the phone card's second line.
          gridArea: { xs: "status", sm: "auto" },
          justifySelf: "start",
          width: 92,
          height: CHIP_HEIGHT,
          borderRadius: status.chipRadius,
        }}
      />
      <Box sx={styles.progressRow}>
        <Skeleton
          variant="rounded"
          sx={{
            flex: 1,
            height: status.barHeight,
            borderRadius: status.barRadius,
          }}
        />
        {/* Stands in for the "0/1" tally, and sets this line's height. */}
        <Skeleton
          variant="text"
          sx={
            [
              styles.progressNote,
              { width: 26, flexShrink: 0 },
            ] as SxProps<Theme>
          }
        />
      </Box>
    </Box>

    {withTags && (
      <Box sx={cells.tagsCell}>
        <Skeleton
          variant="rounded"
          sx={{ width: "64%", height: tags.height, borderRadius: tags.radius }}
        />
      </Box>
    )}

    <Box sx={cells.createdCell}>
      <Skeleton variant="text" sx={{ width: "70%" }} />
    </Box>

    <Box sx={cells.expiresCell}>
      <Skeleton
        variant="text"
        sx={[cells.expiresDate, { width: "70%" }] as SxProps<Theme>}
      />
    </Box>

    <Box sx={styles.actionsColumn}>
      <Skeleton variant="circular" width={kebab.size} height={kebab.size} />
    </Box>
  </Box>
);

export default SentRequestRowSkeleton;
